import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';

const SmartTableGrid = ({
                            data = [],
                            columns = [],
                            totalsRow = null,
                            enableSearch = true,
                            enablePagination = true,
                            enableItemsPerPage = true,
                            enableFilters = {},
                            itemsPerPageOptions = [10, 25, 50, 100],
                            customControls = null,
                        }) => {
    const initialSortColumn = columns.find(col => col.defaultSort);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);
    const [sortKey, setSortKey] = useState(initialSortColumn?.key || null);
    const [sortDirection, setSortDirection] = useState(initialSortColumn?.defaultSort || null);
    const [filters, setFilters] = useState({});
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        let filtered = Array.isArray(data) ? [...data] : [];

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== "All") {
                filtered = filtered.filter(row => row[key] === value);
            }
        });

        if (searchQuery) {
            filtered = filtered.filter(row =>
                columns.some(col =>
                    col.searchable && String(row[col.key] ?? "").toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        }

        if (sortKey && sortDirection) {
            filtered.sort((a, b) => {
                const column = columns.find(col => col.key === sortKey);
                const aVal = column?.sortValue ? column.sortValue(a) : getNestedValue(a, sortKey);
                const bVal = column?.sortValue ? column.sortValue(b) : getNestedValue(b, sortKey);

                return String(aVal).localeCompare(String(bVal), undefined, {
                    numeric: true,
                    sensitivity: "base"
                }) * (sortDirection === "asc" ? 1 : -1);
            });
        }

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [data, filters, searchQuery, sortKey, sortDirection]);

    const getNestedValue = (obj, path) => path.split(".").reduce((o, p) => (o ? o[p] : null), obj);

    const toggleSort = (key) => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDirection("desc");
        } else if (sortDirection === "desc") {
            setSortDirection("asc");
        } else if (sortDirection === "asc") {
            setSortKey(null);
            setSortDirection(null);
        }
    };

    const getSortIcon = (col) => {
        if (!col.sortable) return null;

        return (
            <div className="table-header-sort-icons">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="10" height="10">
                    <path
                        d="M201.4 137.4c12.5-12.5 32.8-12.5 45.3 0l160 160c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L224 205.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160z" />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="10" height="10">
                    <path
                        d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                </svg>
            </div>
        );
    };

    const paginatedData = enablePagination ? (Array.isArray(filteredData) ? filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : []) : (Array.isArray(filteredData) ? filteredData : []);
    const totalPages = Math.ceil((filteredData?.length || 0) / itemsPerPage);

    const PaginationControls = () => (
        <div className="table-footer d-flex flex-row align-items-center justify-content-end">
            <div className="d-flex flex-row mr-3 pagination">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>{__("First")}</button>
                <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}>&larr;</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p >= currentPage - 2 && p <= currentPage + 2)
                    .map(p => (
                        <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            className={p === currentPage ? "active" : ""}
                        >
                            {p}
                        </button>
                    ))}
                <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}>&rarr;</button>
                <button onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}>{__("Last")}</button>
            </div>
        </div>
    );

    const getColumnStyle = (col) => ({
        flex: col.flex ? col.flex : `0 0 ${col.width || "120px"}`,
        minWidth: col.width || "120px",
        maxWidth: col.width || undefined,
        whiteSpace: "normal",
        wordBreak: "break-word"
    });

    return (
        <div className="smart-table col-xl-12">
            <div className="table-controls row mb-3 ml-0 mr-0 pl-3">
                <div className="col d-flex flex-wrap align-items-center p-0">
                    {enableSearch && (
                        <div className="mr-4">
                            <span className="mr-2 text-nowrap">{__("Search Location")}:</span>
                            <input
                                type="search"
                                className="search-business"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={__("Search")}
                            />
                        </div>
                    )}

                    {Object.entries(enableFilters).map(([key, values]) => {
                        const label = columns.find(col => col.key === key)?.label || key;
                        return (
                            <div key={key} className="mr-4">
                                <span className="mr-2">{__("Filter by")} {__(label)}:</span>
                                <select onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}>
                                    {values.map((val) => (
                                        <option key={val} value={val}>{__(val)}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}

                    {enableItemsPerPage && (
                        <div className="mr-4">
                            <label className="mr-1 mb-0" htmlFor="itemsPerPage">{__("Items per page")}:</label>
                            <select id="itemsPerPage" value={itemsPerPage}
                                    onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}>
                                {itemsPerPageOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {customControls}
                </div>
                <div className="col-auto">
                    {enablePagination && totalPages > 1 && <PaginationControls />}
                </div>
            </div>

            <div className="table-section overflow-auto">
                <div className="table-header table-min-1200 d-flex flex-row ml-0 mr-0 flex-wrap">
                    {columns.map(col => (
                        <div
                            key={col.key}
                            className={`col-1 col-1--narrow ${col.headerClass ?? ""}`}
                            onClick={() => col.sortable && toggleSort(col.key)}
                            style={getColumnStyle(col)}
                            data-sort={sortDirection}
                            data-issorted={sortKey === col.key}
                        >
                            <div className="table-header-label-wrapper"
                                 style={{ whiteSpace: "normal", wordBreak: "keep-all" }}>
                                <span>{__(col.label)}</span> {getSortIcon(col)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="table-body table-min-1200">
                    {paginatedData.length > 0 ? paginatedData.map((row, index) => (
                        <div className="row ml-0 mr-0 flex-wrap" key={index}>
                            {columns.map(col => (
                                <div
                                    key={col.key}
                                    className={`col-1 col-1--narrow ${row.isTotalRow ? "font-bold" : ""}`}
                                    style={getColumnStyle(col)}
                                >
                                    {col.render
                                        ? col.render(getNestedValue(row, col.key), row)
                                        : getNestedValue(row, col.key) ?? ""}
                                </div>
                            ))}
                        </div>
                    )) : (
                        <h2 className="d-block text-center w-100 text-no-results">{__("No Items Found")}.</h2>
                    )}
                </div>

                <div className="table-footer table-min-1200 mb-4 py-2">
                    {totalsRow && (
                        <div className="row ml-0 mr-0 font-weight-bold flex-wrap">
                            {columns.map(col => (
                                <div key={col.key} className="col-1 col-1--narrow" style={getColumnStyle(col)}>
                                    {col.render
                                        ? col.render(getNestedValue(totalsRow, col.key), {
                                            ...totalsRow,
                                            isTotalRow: true
                                        })
                                        : <strong>{getNestedValue(totalsRow, col.key) ?? ""}</strong>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {enablePagination && totalPages > 1 && <PaginationControls />}
        </div>
    );
};

export default SmartTableGrid;
